import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, CalendarCheck, Users, MapPin, BarChart3 } from "lucide-react";

import { emptyProposal, proposalErrors, proposalRules, groupName, groupHref } from "@/lib/v3Groups";
import { Unavailable, ErrorNote } from "../components/kit";
import { V3Title, V3Columns, V3Actions, V3Field, StatusBanner, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { V3Select, V3TextArea, ResultTable, ResultRow, V3_CARD } from "../components/v3app";
import { InfoBox, StagePill, SegmentTabs } from "../components/v3people";
import { useMembership, GroupAside } from "../components/v3groups";
import { WithGroup } from "./GroupSections";

const Blocked = ({ id, children }) => <div className="mt-[12px]" id={id}><Unavailable testid={id}>{children}</Unavailable></div>;
const POLICY_LABEL = { bestaetigung: "Geschlossen · Beitritt auf Anfrage", offen: "Offen · Beitritt ohne Bestätigung" };

// M16: proposal form with a review step; submitting needs POST /member/group-proposals (D-57) – nothing is sent or stored.
export function GroupPropose() {
  const [d, setD] = useState(emptyProposal);
  const [review, setReview] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (k) => (e) => { setD((x) => ({ ...x, [k]: e.target.value })); setReview(false); };
  const check = () => { const e = proposalErrors(d); setErrors(e); setReview(!Object.keys(e).length); };
  return (
    <div data-m="M16" data-testid="group-propose">
      <V3Title title="Gruppe vorschlagen und verwalten" intro="Schlage einen Kreis mit verständlichen Regeln und klarer Verantwortung vor." testid="group-propose-title" />
      <V3Columns layout="app" aside={<GroupAside title="Ausgewählter Vorgang" text={d.name.trim() || "Neuer Kreisvorschlag"} />} main={<>
        <V3Field id="group-propose-name" label="Kreisname" value={d.name} onChange={set("name")} error={errors.name} placeholder="Noch nicht eingegeben" />
        <V3TextArea id="group-propose-description" label="Beschreibung" max={500} value={d.description} onChange={set("description")} error={errors.description} rows={3} placeholder="Worum geht es im Kreis?" />
        <V3Select id="group-propose-policy" label="Sichtbarkeit" value={d.policy} onChange={set("policy")} testid="group-propose-policy">
          {Object.entries(POLICY_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </V3Select>
        <V3TextArea id="group-propose-rules" label="Regeln" value={d.rules} onChange={set("rules")} error={errors.rules} rows={3} hint="Eine Regel pro Zeile, höchstens 10." placeholder="Respekt, Privatsphäre und Zustimmung bei Fotos" />
        {review && <div data-testid="group-propose-review">
          <InfoBox title="Vorschlag prüfen" text="So würde der Vorschlag eingereicht. Über die Einrichtung entscheidet das VISIBILIS-Team." />
          <DetailList>
            <DetailRow label="Kreisname" value={d.name.trim()} testid="group-propose-review-name" />
            <DetailRow label="Sichtbarkeit" value={POLICY_LABEL[d.policy]} testid="group-propose-review-policy" />
            <DetailRow label="Regeln" value={proposalRules(d.rules).join(" · ")} testid="group-propose-review-rules" />
          </DetailList>
          <Blocked id="group-propose-blocked">Kreise vorschlagen kann der gemeinsame Core noch nicht. Der Vorschlag wird weder gesendet noch gespeichert und geht beim Verlassen verloren.</Blocked>
        </div>}
        <V3Actions>
          {review
            ? <button type="button" disabled aria-describedby="group-propose-blocked" data-testid="group-propose-submit" className={V3_PRIMARY}>Vorschlag einreichen</button>
            : <button type="button" onClick={check} data-testid="group-propose-check" className={V3_PRIMARY}>Kreisvorschlag prüfen</button>}
          <Link to="/kreise" data-testid="group-propose-cancel" className={V3_SECONDARY}>Abbrechen</Link>
        </V3Actions>
      </>} />
    </div>
  );
}

// M74: invitations need GET/POST /member/group-invitations (D-55); opening is never an acceptance.
export function GroupInvitation() {
  return (
    <div data-m="M74" data-testid="group-invitation">
      <V3Title title="Einladung in einen Kreis" intro="Prüfe den Kreis und seine Regeln, bevor du eine Einladung annimmst." testid="group-invitation-title" />
      <V3Columns layout="app" aside={<GroupAside title="Ausgewählter Vorgang" text="Keine Einladung abrufbar" />} main={<>
        <StagePill testid="group-invitation-stage">Vor der Entscheidung</StagePill>
        <DetailList>
          <DetailRow label="Einladung von" value="Noch nicht verfügbar" testid="group-invitation-from" />
          <DetailRow label="Kreis" value="Noch nicht verfügbar" testid="group-invitation-group" />
          <DetailRow label="Sichtbarkeit" value="Noch nicht verfügbar" testid="group-invitation-visibility" />
          <DetailRow label="Annahme" value="Kein automatisches Teilen deines Access Passes" testid="group-invitation-effect-pass" />
        </DetailList>
        <div className="mt-[11px]"><InfoBox title="Wirkung nach Bestätigung" text="Der Vorgang bleibt bis zu deiner ausdrücklichen Bestätigung offen. Öffnen allein ist keine Annahme." testid="group-invitation-effect" /></div>
        <Blocked id="group-invitation-blocked">Einladungen in Kreise liefert der gemeinsame Core noch nicht. Es wird nichts angenommen, abgelehnt oder gespeichert.</Blocked>
        <V3Actions>
          <button type="button" disabled aria-describedby="group-invitation-blocked" data-testid="group-invitation-accept" className={V3_PRIMARY}>Einladung annehmen</button>
          <Link to="/kreise" data-testid="group-invitation-cancel" className={V3_SECONDARY}>Abbrechen</Link>
        </V3Actions>
      </>} />
    </div>
  );
}

function Stat({ icon: Icon, label, testid }) {
  return (
    <div className={`${V3_CARD} px-[12px] pb-[14px] pt-[12px]`} data-testid={testid}>
      <p className="flex items-center gap-[10px] text-[22px] font-bold leading-[30px] text-v3-ink"><Icon className="h-[17px] w-[17px] text-v3-magenta" aria-hidden="true" />–</p>
      <p className="mt-[8px] text-[12.5px] font-bold text-v3-muted">{label}<span className="sr-only">: nicht verfügbar</span></p>
    </div>
  );
}

const REQUEST_TABS = [["anfragen", "Anfragen"], ["bestaetigt", "Bestätigt"], ["warteliste", "Warteliste"]];

// M75: circle responsibility has no member-side role or request list in Core 1.1 (D-56); no persons or numbers are invented.
export function GroupRequests() {
  const [tab, setTab] = useState("anfragen");
  return (
    <div data-m="M75" data-testid="group-requests">
      <V3Title title="Aufnahmeanfragen meines Kreises" intro="Bearbeite Aufnahmeanfragen für den von dir verantworteten Kreis." testid="group-requests-title" />
      <div className="grid grid-cols-2 gap-[11px] lg:grid-cols-4 lg:gap-[12px]">
        <Stat icon={CalendarCheck} label="Bestätigt" testid="group-requests-stat-confirmed" />
        <Stat icon={Users} label="Anfragen offen" testid="group-requests-stat-open" />
        <Stat icon={MapPin} label="Freie Teilnehmendenplätze" testid="group-requests-stat-free" />
        <Stat icon={BarChart3} label="Begleitplätze belegt" testid="group-requests-stat-companions" />
      </div>
      <SegmentTabs items={REQUEST_TABS} value={tab} onChange={setTab} label="Anfragen nach Zustand" testid="group-requests-tabs" />
      <div className="mt-[8px]"><ResultTable testid="group-requests-table" label={REQUEST_TABS.find(([k]) => k === tab)[1]}>
        <ResultRow initial="–" title={REQUEST_TABS.find(([k]) => k === tab)[1]} context="Noch nicht verfügbar" action="" testid="group-requests-empty" />
      </ResultTable></div>
      <Blocked id="group-requests-blocked">Aufnahmeanfragen entscheidet derzeit nur das VISIBILIS-Team im Control OS. Eine Kreisverantwortung kennt die Mitglieder-App noch nicht; es werden keine Personen oder Zahlen angezeigt.</Blocked>
      <V3Actions className="mt-[17px]">
        <button type="button" disabled aria-describedby="group-requests-blocked" data-testid="group-requests-review" className={V3_PRIMARY}>Aufnahmeanfrage prüfen</button>
      </V3Actions>
      <div className="mt-[14px]"><InfoBox icon={Lock} title="Private Angaben" text="Eine Teilnahmebestätigung öffnet keine privaten Passfelder. Dafür ist eine gesonderte persönliche Freigabe erforderlich." testid="group-requests-private" /></div>
    </div>
  );
}

function Leave({ listed }) {
  const navigate = useNavigate();
  const m = useMembership(listed);
  const [error, setError] = useState("");
  const g = m.group;
  const leave = async () => {
    setError("");
    const o = await m.act("leave");
    if (!o) return;
    if (o.result === "failed") { setError(o.message); return; }
    if (o.result === "success") navigate(groupHref(g), { replace: true });
  };
  const note = m.outcome && m.outcome.result !== "success" && m.outcome.message;
  return (
    <div data-m="M76" data-testid="group-leave-view">
      <V3Title title="Kreis verlassen" intro="Du kannst später erneut um Aufnahme bitten." testid="group-leave-title" />
      <V3Columns layout="app" aside={<GroupAside title="Ausgewählter Vorgang" text={groupName(g)} />} main={<>
        <InfoBox title="Deine Bestätigung ist erforderlich" text="Prüfe den angezeigten Vorgang und die konkreten Auswirkungen." testid="group-leave-confirm-note" />
        {note && <div role="status" data-testid="group-membership-note"><StatusBanner tone="notice" title={m.outcome.result === "unknown" ? "Status nicht bestätigt" : "Hinweis"} text={note} /></div>}
        <DetailList>
          <DetailRow label="Kreis" value={groupName(g)} testid="group-leave-name" />
          <DetailRow label="Nach dem Austritt" value="Interne Beiträge und Gruppenchat nicht mehr verfügbar" testid="group-leave-after" />
          <DetailRow label="Eigene Beiträge" value="Verbleib vor dem Austritt prüfen" to={`/community?bereich=kreis&kreis=${encodeURIComponent(g.id)}`} testid="group-leave-posts" />
          <DetailRow label="Persönliche Freigaben" value="Gesondert in Meine Freigaben verwalten" to="/access-pass/freigaben" testid="group-leave-grants" />
        </DetailList>
        {error && <div className="mt-[12px]"><ErrorNote testid="group-action-error">{error}</ErrorNote></div>}
        <V3Actions>
          {note
            ? <button type="button" onClick={m.recheck} disabled={m.busy} data-testid="group-recheck" className={V3_PRIMARY}>Status erneut prüfen</button>
            : <button type="button" onClick={leave} disabled={m.busy} data-testid="group-leave-confirm" className={V3_PRIMARY}>{m.busy ? "Wird übermittelt …" : "Kreis verlassen"}</button>}
          <Link to={groupHref(g)} data-testid="group-leave-stay" className={V3_SECONDARY}>Im Kreis bleiben</Link>
        </V3Actions>
      </>} />
    </div>
  );
}

// M76: POST /member/groups/{id}/leave, success only after a fresh GET shows the membership cleared.
export function GroupLeave() {
  return <WithGroup membersOnly>{(g) => <Leave key={g.id} listed={g} />}</WithGroup>;
}
